import { Injectable } from "@angular/core";
import { AbstractControl, FormArray, FormBuilder, FormGroup } from "@angular/forms";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { LOG_CATEGORY, LOG_SUB_TYPE, LOG_TYPE, createAuditLogKey } from "@app/audit-log/services/audit-log.constants";
import { AuditLogService } from "@app/audit-log/services/audit-log.service";
import { Customer } from "@app/customer/model/customer";
import { CustomerService } from "@app/customer/services/customer.service";
import { CustomerActions, CustomerSelectors } from "@app/customer/store";
import { EntityService } from "@app/entity/services/entity.service";
import { accountRoutePaths } from "@app/project/project-routing-constants";
import { RouterActions } from "@app/root-store";
import { SnackbarActions } from "@app/root-store/actions";
import { UserExistsDialogComponent } from "@app/shared/components/user-exists-dialog/user-exists-dialog.component";
import { ClareStatus, ClareStatuses } from "@app/shared/model/clare-status.model";
import { FormatPhonePipe } from "@app/shared/resources/phone-formatter.pipe";
import { HttpResponseHandler } from "@app/shared/services/http-response-handler";
import { phoneValid } from "@app/shared/validators/phoneValid";
import { UserManagementSelectors } from "@app/user/store";
import { UserManagementState } from "@app/user/store/user-management.state";
import { Store, select } from "@ngrx/store";
import { TypedAction } from "@ngrx/store/src/models";
import { Observable, Subject } from "rxjs";
import { filter, map, share, switchMap, take, takeUntil, tap } from "rxjs/operators";
import { Project } from "../model/project";
import { ProjectDispatchers } from "../store/dispatchers/project.dispatchers";
import { ProjectDbService } from "./project-db.service";
import { ProjectService } from "./project.service";

@Injectable({
    providedIn: "root",
})
export class ProjectCustomerService {
    private readonly unsubscribeFromCustomerChangesSubscription$ = new Subject<void>();

    /**
     * Unsubscribe by calling unsubscribeFromCustomerChangesSubscription()
     *
     * @description Selects the entities, dispatches an action to get the entity users, and
     * takes the value from the userObj field and returns the user found in the state.
     * @param {FormGroup} formGroup the form group to perform the actions on
     * @returns {Observable<User>} the found user
     */
    subscribeToCustomerChanges$(formGroup: FormGroup) {
        this.entityService
            .selectEntities$()
            .pipe(
                takeUntil(this.unsubscribeFromCustomerChangesSubscription$),
                switchMap(() =>
                    this.customerService
                        .selectAuthEntityIdAndDispatchEntityCustomers()
                        .pipe(takeUntil(this.unsubscribeFromCustomerChangesSubscription$))
                )
            )
            .subscribe();
        return formGroup.controls.userObj.valueChanges.pipe(
            takeUntil(this.unsubscribeFromCustomerChangesSubscription$),
            map(value => (typeof value === "string" ? value : value.email)),
            switchMap((value: string) =>
                this.getCustomersByEmailAndEntityId(value, +formGroup.controls.entityId.value).pipe(
                    take(1),
                    map(users => {
                        // only one user was returned
                        if (users.length === 1 && users[0].email && users[0].email.toLowerCase() === value) {
                            formGroup.controls.email.setAsyncValidators(null);
                            formGroup.controls.gitkitId?.setValue(users[0].gitkitId);
                            this.selectedEmailValue({ option: { value: users[0] } }, formGroup);
                            if (!users[0].accountNumber) {
                                formGroup.controls.accountNumber.enable();
                            }
                        } else {
                            formGroup.controls.email.setValue(value);

                            if (!!value && users.length === 0) {
                                formGroup.controls.email.setAsyncValidators(this.emailDoesNotExist);
                                formGroup.controls.email.updateValueAndValidity();
                            }
                            formGroup.controls.firstName.enable();
                            formGroup.controls.lastName.enable();
                            formGroup.controls.phone.enable();
                            formGroup.controls.accountNumber.enable();
                            formGroup.controls.id.setValue(null);
                            formGroup.controls.gitkitId?.setValue(null);
                        }

                        if (formGroup.controls.email.errors) {
                            formGroup.controls.userObj.setErrors({ invalid: true });
                        } else {
                            formGroup.controls.userObj.setErrors(null);
                        }

                        return users;
                    })
                )
            )
        );
    }

    public unsubscribeFromCustomerChangesSubscription(): void {
        this.unsubscribeFromCustomerChangesSubscription$.next();
        this.unsubscribeFromCustomerChangesSubscription$.complete();
    }

    /**
     * @description Selects customer by email and if the entity ID matches
     * @param {string} email the customer email to search for
     * @param {number} entityId the entity ID to match
     * @returns {Observable<User>} the found user
     */
    getCustomersByEmailAndEntityId(email: string, entityId: number) {
        return this.store.pipe(
            select(CustomerSelectors.getCustomerList),
            map(customer => {
                const users = customer.sort((a, b) => (a.email || "").localeCompare(b.email || ""));
                return users.filter(
                    user => user.email && user.email.toLowerCase().indexOf(email.toLowerCase()) !== -1 && user.entityId === entityId
                );
            })
        );
    }

    /**
     * @description Opens a dialog using the UserExistsDialogComponent and subscribes to the close events
     * @param {number} projectId the id of the project to search for
     */
    openUserExistsDialog(projectId: number) {
        const dialogRef: MatDialogRef<UserExistsDialogComponent> = this.dialog.open(UserExistsDialogComponent, {
            width: "350px",
            disableClose: false,
        });

        dialogRef
            .afterClosed()
            .pipe(take(1))
            .subscribe(result => {
                if (result) {
                    this.store.dispatch(
                        RouterActions.routerGo({
                            path: ["/accounts/edit", projectId, "summary"],
                        })
                    );
                    this.dialog.closeAll();
                }
            });
    }

    /**
     * @description Patches values to a form group and disables the user fields to keep them from updating the
     * user info. Used when trying to display info for an existing contact, but we do not want them to update the info
     * from this particular form.
     * @param event the event
     * @param event.option the option in the event
     * @param {Record<string, any>} event.option.value the user values to patch to the form
     * @param {FormGroup} formGroup
     * @returns {Observable<ProjectData>} an account the user already exists on (if applicable)
     */
    selectedEmailValue(event: { option: { value: Record<string, any> } }, formGroup: FormGroup): void {
        const formValues = event.option.value;
        formGroup.patchValue(formValues, { emitEvent: false });
        formGroup.controls.firstName.disable();
        formGroup.controls.lastName.disable();
        formGroup.controls.accountNumber.disable();

        // previously optional field, only disable if saved value
        const phoneFormat = "x (xxx) xxx-xxxx";
        const phone = formGroup.controls.phone;
        if (phone.value) {
            phone.setValue(new FormatPhonePipe().transform(phone.value, phoneFormat));
            phone.disable();
        }
        if (!formGroup.controls.phoneType.value) {
            formGroup.patchValue({ phoneType: "Home" });
        }

        // add phone2 and phone3 into form array
        const additionalPhoneNumbers = formGroup.controls.additionalPhoneNumbers as FormArray;
        if (additionalPhoneNumbers) {
            if (formValues.phone2?.length) {
                additionalPhoneNumbers.push(
                    this.createAdditionalPhoneNumberGroup(
                        new FormatPhonePipe().transform(formValues.phone2, phoneFormat),
                        formValues.phone2Type || ""
                    )
                );
            }

            if (formValues.phone3?.length) {
                additionalPhoneNumbers.push(
                    this.createAdditionalPhoneNumberGroup(
                        new FormatPhonePipe().transform(formValues.phone3, phoneFormat),
                        formValues.phone3Type || ""
                    )
                );
            }
        }
    }

    public createAdditionalPhoneNumberGroup(phone: string, phoneType: string): FormGroup {
        return this.formBuilder.group({
            phone: [phone, phoneValid],
            phoneType,
        });
    }

    /**
     * @description An async validator that checks if the email address exists in the users in the state
     * @param {AbstractControl} control the control with the email address to check
     * @returns {Observable<{ emailExists: string } | undefined>} an error object or undefined
     */
    // prettier-ignore
    emailDoesNotExist = (control: AbstractControl): Observable<{ emailExists: string } | undefined> => this.store.pipe(
            select(UserManagementSelectors.checkUserExistenceByEmail(control.value)),
            map(users => {
                if (users) {
                    return { emailExists: "This email is in use by a user. Enter a different email." };
                }
                    return undefined;

            })
        );

    onGoToMoveInOutWizard(isMoveOut = false, projectStatus: ClareStatus, projectId: number) {
        let config = {} as any;
        if (!projectStatus || projectStatus[0] === ClareStatuses.Unconfigured) {
            config = {
                id: "unconfigured",
                data: {
                    icon: "cc-exclamation",
                    title: `This system has not been configured.`,
                    message: `A controller must be added to the account before the Move ${
                        isMoveOut ? "Out" : "In"
                    } process can be initiated.`,
                    buttons: [
                        {
                            text: "Cancel",
                            method: "close",
                        },
                        {
                            text: "Add Controller",
                            buttonColor: "primary",
                            method: {
                                emit: () => {
                                    this.goToServicePlanPage(projectId);
                                    this.dialog.closeAll();
                                },
                            },
                        },
                    ],
                },
            };
        } else if (projectStatus[0] === ClareStatuses.Offline) {
            const msg = isMoveOut
                ? `Move Out process is limited and cannot be fully completed while the panel is offline.`
                : `Move In cannot be started because the Clare System is not connected to the internet. Please reconnect the Clare System panel to resolve the offline condition then resume the Move In process.`;
            const buttonArr = isMoveOut
                ? [
                      {
                          text: "Cancel",
                          method: "close",
                      },
                      {
                          text: "Continue",
                          buttonColor: "primary",
                          method: {
                              emit: () => this.onMoveOut(projectId),
                          },
                      },
                  ]
                : [
                      {
                          text: "OK",
                          buttonColor: "primary",
                          method: "close",
                      },
                  ];
            config = {
                id: "cancel-process",
                data: {
                    icon: "cc-exclamation",
                    title: `Panel is Offline`,
                    message: msg,
                    buttons: buttonArr,
                },
            };
        } else {
            config = {
                id: "move-out",
                data: {
                    title: `Continue to Move ${isMoveOut ? "Out" : "In"} Customer?`,
                    message: `You will be taken through the step-by-step process for moving ${isMoveOut ? "out" : "in"} a customer.`,
                    buttons: [
                        {
                            text: "Cancel",
                            method: "close",
                        },
                        {
                            text: "Continue",
                            buttonColor: "primary",
                            method: {
                                emit: () => {
                                    if (isMoveOut) {
                                        this.onMoveOut(projectId);
                                    } else {
                                        this.onMoveIn(projectId);
                                    }
                                },
                            },
                        },
                    ],
                },
            };
        }
        return config;
    }

    onMoveOut(id: number) {
        this.store.dispatch(
            RouterActions.routerGo({
                path: [`/${accountRoutePaths.root}${accountRoutePaths.edit}`, id, "customer-info", "move-out"],
            })
        );
    }

    onMoveIn(id: number) {
        this.store.dispatch(
            RouterActions.routerGo({
                path: [`/${accountRoutePaths.root}${accountRoutePaths.edit}`, id, "customer-info", "move-in"],
            })
        );
    }
    goToServicePlanPage(projectId: number) {
        this.store.dispatch(
            RouterActions.routerGo({
                path: ["/accounts/edit", projectId, "service-plan"],
            })
        );
    }
    /**
     * @description Dispatches any action
     * @param {TypedAction} action the action
     */
    dispatch(action: TypedAction<any>) {
        this.store.dispatch(action);
    }

    /**
     * @description assigns a customer to a project
     * @param {Project} project the project to assign the customer to
     * @param {Customer} customer the user to assign to the project
     */
    public onboardCustomerToProject(project: Project, customer: Customer): void {
        if (project.claresystem) {
            this.assignUserToClareSystem(project, customer);
            this.updateCustomerInviteCode(customer);
            this.projectDispatchers.dispatchClearLocationUsers(project.claresystem);
        } else {
            this.projectDispatchers.dispatch(
                SnackbarActions.openSnackbar({
                    message: new HttpResponseHandler(null, null, "Please add a controller before inviting your customer"),
                })
            );
        }
    }

    /**
     * @description assigns a user to a clare system
     * @param {Project} project the project containing the system to assign the customer to
     * @param {Customer} customer the user to assign to the system
     */
    public assignUserToClareSystem(project: Project, customer: Customer): void {
        this.projectDbService
            .connectUserToClaresystem(customer, project.claresystem)
            .pipe(
                take(1),
                tap(_ => {
                    this.projectDispatchers.dispatchProjectUsers(project.id);
                })
            )
            .subscribe();
    }

    /**
     * @description updates a customer invite code
     * @param {Customer} customer the user whose invite code to update
     */
    public updateCustomerInviteCode(customer: Customer) {
        customer.inviteCode = this.customerService.generateCustomerInviteCode();
        this.projectDispatchers.dispatch(CustomerActions.updateInviteCode({ customer }));
    }

    /**
     * @description sends customer modified email
     * @param {Customer} newCustomer the new customer
     * @param {Customer} previousCustomer the previous customer
     */
    public sendCustomerModifiedEmail(newCustomer: Customer = {} as Customer, previousCustomer: Customer = {} as Customer) {
        if (previousCustomer) {
            this.dispatchCustomerModifiedEmail(newCustomer, previousCustomer);
        }
    }

    /**
     * @description dispatches customer modified email action
     * @param {Customer} newCustomer the new customer
     * @param {Customer} previousCustomer the previous customer
     */
    public dispatchCustomerModifiedEmail(newCustomer: Customer = {} as Customer, previousCustomer: Customer = {} as Customer): void {
        this.projectService
            .getActiveProjectEntity$()
            .pipe(
                share(),
                filter(e => !!e),
                take(1),
                tap(entity => {
                    const payload = {
                        removedUser: {
                            email: previousCustomer.email,
                            firstName: previousCustomer.firstName,
                            lastName: previousCustomer.lastName,
                        },
                        newUser: {
                            firstName: newCustomer.firstName,
                            lastName: newCustomer.lastName,
                        },
                        entityName: entity?.name,
                    };
                    this.projectDispatchers.dispatch(CustomerActions.sendCustomerModifiedLocationEmail(payload));
                })
            )
            .subscribe();
    }

    logAuditEvt(logData: any) {
        const logKey = createAuditLogKey(LOG_CATEGORY.ACCOUNT, LOG_TYPE.ACCOUNT, LOG_SUB_TYPE.CHANGE_PRIMARY_CUSTOMER);
        this.auditLogService.logEvent(logKey, logData);
    }
    constructor(
        private readonly store: Store<UserManagementState>,
        private readonly dialog: MatDialog,
        private readonly customerService: CustomerService,
        private readonly entityService: EntityService,
        private readonly projectDbService: ProjectDbService,
        private readonly projectDispatchers: ProjectDispatchers,
        private readonly projectService: ProjectService,
        private readonly formBuilder: FormBuilder,
        private readonly auditLogService: AuditLogService
    ) {}
}
